import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import dynamic from 'next/dynamic';
import FollowingOverlay from '../components/FollowingOverlay/FollowingOverlay';
import { DashboardPageWrapper } from '@fabric-app/business/dashboard/components/DashboardPageWrapper';
import { SharedHoverProvider } from '../hooks/sharedHover';
import useUIStore from '../store/ui';
import { useSearchOverlay } from '@fabric-app/business/search/hooks/useSearchOverlay';
import { useAnalytics } from '@fabric-app/business/analytics/hooks/useAnalytics';
import { AnalyticsEvents } from '@fabric-app/business/analytics/analytics.types';
import { useQueryResourceList } from '@fabric-app/business/resources/queries/useQueryResourceList';
import { DashboardContent } from '@fabric-app/business/dashboard/components/DashboardContent';
import {
  spaceViewModes,
  type ResourceDetail,
  type ResourceDetailFolder,
} from '@fabric-app/types/resources';
import useMobileSelectionStore from '@fabric-app/business/store/mobileSelection';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useQueryFolder } from '@fabric-app/business/resources/queries/useQueryFolder';
import { useResourcePermissionsByCurrentUser } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { useQueryFolderLabels } from '@fabric-app/business/labels/queries/useQueryFolderLabels';
import { useSocketIOParentRoomEventListener } from '@fabric-app/business/socket.io/ParentRoom/useSocketIOParentRoomEventListener';
import { isResourceDetailFolder } from '@fabric-app/business/resources/utils/resourceDetailGuards';
import { ResourceListContextPreferencesProvider } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import { SpaceHeader } from '../modules/spaces/pages/SpaceView/SpaceHeader';
import { withSocketIOParentRoomProvider } from '@fabric-app/business/socket.io/ParentRoom/SocketIOParentRoomProvider';
import { withSpacerRoomProvider } from '@fabric-app/business/multiplayer/config/spaces.config';
import { ResourceFolderEditorialHeader } from '@fabric-app/business/resources/components/ResourceFolderEditorialHeader';
import { useListViewPreferences } from '@fabric-app/business/resources/hooks/useListViewPreferences';
import { useResourceSwitcher } from '@fabric-app/business/resource-detail/contexts/resourcesSwitcher';
import { NextSeo } from 'next-seo';
import { useQueryClient } from '@tanstack/react-query';
import { rootQueryKeys } from '@fabric-app/business/resource-roots/queries/rootQueryKeys';
import { PublishedFlair } from '@fabric-app/business/permission/components/PublishedFlair/PublishedFlair';

const FdocList = dynamic(() => import('../components/FdocList/FdocList'), { ssr: false });

const ExpandedSpaceBase: React.FC<{
  resourceId: string;
  overrideOnClose?: VoidFunction | null;
  onClickFolder?: (resourceFolder: ResourceDetailFolder | string) => void;
  onClickResource?: (resource: ResourceDetail) => void;
}> = ({ resourceId: spaceId, overrideOnClose, onClickFolder, onClickResource }) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const { disableMobileSelectMode } = useMobileSelectionStore(
    (state) => ({
      disableMobileSelectMode: state.disableMobileSelectMode,
    }),
    shallow,
  );

  const { isOverlayOpen: isSearchOverlayOpen } = useSearchOverlay();

  const { zoomLevel, setZoomLevel } = useUIStore(
    (s) => ({
      zoomLevel: s.zoomLevel,
      setZoomLevel: s.setZoomLevel,
    }),
    shallow,
  );

  useRunOnce(() => {
    track(AnalyticsEvents.OpenSpace, { spaceId });
  });

  const activityPanelState = useBoolState();

  /**********************************************************************************************************
   * Fetch folder
   */

  const { folder, isError } = useQueryFolder({ resourceId: spaceId });

  const { savePreferences, preferences } = useListViewPreferences(folder);

  const filters = {
    ...preferences?.filters,
    parentId: spaceId,
  };

  const { disabledActions } = useResourcePermissionsByCurrentUser(folder);

  /*******************************************************************************************************
   * Client side redirects
   */
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (isError) {
      router.replace('/404');
      return;
    }

    /**
     * if integration, we use folder view
     */
    if (folder?.root?.type === 'INTEGRATION') {
      router.replace(`/folders/${folder.id}`);
    }
  }, [isError, router, folder]);

  /*********************************************************************************************************
   * labels
   */
  const { data: rootLabels } = useQueryFolderLabels(folder?.root?.id);

  /*********************************************************************************************************
   * resources
   */
  const {
    resources,
    total: totalResources,
    isLoading: isLoadingResources,
    fetchNextPage: _fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useQueryResourceList(filters, {
    enabled: !isSearchOverlayOpen || !spaceId,
    refetchInterval: false,
  });

  const fetchNextPage = !isFetchingNextPage && hasNextPage ? _fetchNextPage : undefined;

  useSocketIOParentRoomEventListener('RESOURCES:RELOAD', () => {
    refetch();
  });

  /************************************************************************************************************************
   * switcher stuff
   */
  const { setResourceIds: setSwitcherResourceIds } = useResourceSwitcher();

  useEffect(() => {
    if (isSearchOverlayOpen) return;
    setSwitcherResourceIds(
      resources.filter((f) => !isResourceDetailFolder(f)).map((n) => n.id),
      {
        contextId: `SPACE-${spaceId}`,
        fetchNextPage,
      },
    );
  }, [resources, setSwitcherResourceIds, isSearchOverlayOpen, spaceId, fetchNextPage]);

  /************************************************************************************************************************/

  const emptyStateTitle = useMemo(() => {
    if (preferences && preferences?.filters?.kind) {
      return `No matches for selected kinds`;
    }
    return undefined;
  }, [preferences]);

  /* Disable the mobile selection when the component unmounts */
  useEffect(() => () => disableMobileSelectMode(), [disableMobileSelectMode]);

  const queryClient = useQueryClient();

  return (
    <ResourceListContextPreferencesProvider
      preferences={preferences}
      savePreferences={savePreferences}
      availableViewModes={spaceViewModes}
    >
      {({ viewMode }) => (
        <DashboardPageWrapper data-id="space">
          <SpaceHeader
            onBackClick={overrideOnClose}
            listId={spaceId}
            onClickActivtyToggle={activityPanelState.handleToggle}
            resources={resources}
            onClickFolder={onClickFolder}
          />
          <NextSeo title={folder?.name || undefined} />

          <SharedHoverProvider>
            <DashboardContent
              innerProps={{
                disablePadding: true,
                bottomPaddingOffset: '0px',
                gap: 'none',
                ...(viewMode === 'Kanban' && {
                  style: {
                    height: '100%',
                    overflow: 'hidden',
                  },
                }),
              }}
            >
              {folder && <PublishedFlair resource={folder} type="space" />}
              {folder && viewMode !== 'Kanban' && (
                <ResourceFolderEditorialHeader
                  viewMode={viewMode}
                  folder={folder}
                  onRename={() => {
                    queryClient.invalidateQueries({
                      queryKey: rootQueryKeys.resourceRootList(),
                    });
                  }}
                />
              )}
              {folder ? (
                <FdocList
                  queryParams={filters}
                  layoutId={`space-${spaceId}`}
                  canMultiSelect={!isSearchOverlayOpen && viewMode !== 'Kanban'}
                  viewMode={viewMode}
                  resources={resources}
                  resourcesTotalCount={totalResources}
                  isLoading={isLoadingResources}
                  canModifyContent={!disabledActions.modifyChildResources}
                  onInfiniteTrigger={fetchNextPage}
                  colorLabels={rootLabels}
                  spaceId={spaceId}
                  zoomLevel={zoomLevel}
                  setZoomLevel={setZoomLevel}
                  emptyStateTitle={emptyStateTitle}
                  parent={folder}
                  onClickFolder={onClickFolder}
                  onClickResource={onClickResource}
                />
              ) : (
                <div style={{ display: 'flex', flexGrow: 1 }} />
              )}
            </DashboardContent>

            <FollowingOverlay />
          </SharedHoverProvider>
        </DashboardPageWrapper>
      )}
    </ResourceListContextPreferencesProvider>
  );
};

export const ExpandedSpace = withSocketIOParentRoomProvider(
  withSpacerRoomProvider(ExpandedSpaceBase, 'space'),
);
export default ExpandedSpace;
